import { z } from "zod";

/**
 * BeFlow's own invite/recovery emails (see
 * `openspec/changes/prefetch-safe-auth-email-links/design.md` decision 1)
 * link to `{FRONTEND_BASE_URL}/reset-password#token_hash=<hex>&type=invite|recovery`
 * instead of Supabase's own `/auth/v1/verify` action link. No `.strict()`:
 * the schema only needs to recognize this shape, not reject a hash that
 * carries other params alongside it.
 */
export const resetPasswordHashSchema = z.object({
  token_hash: z.string().min(1),
  type: z.enum(["invite", "recovery"]),
});

export type ResetPasswordHashType = z.infer<typeof resetPasswordHashSchema>["type"];

export type ResetPasswordHashResult =
  | { kind: "absent" }
  | { kind: "invalid" }
  | { kind: "valid"; token_hash: string; type: ResetPasswordHashType };

function paramsFromHash(hash: string): URLSearchParams {
  return new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
}

/**
 * Reads `token_hash`/`type` out of a URL fragment.
 *
 * - "absent": no `token_hash` key at all — a visitor who isn't on this
 *   flow (a legacy implicit-flow link, or a plain anonymous visit).
 *   `ResetPasswordPage` falls through to its session-based rendering for
 *   this case instead of treating it as an invalid link.
 * - "invalid": `token_hash` is present but `type` is missing, blank, or
 *   neither `invite` nor `recovery`. Never guessed at or defaulted to
 *   `recovery` (design.md decision 4's rejected alternative) — guessing
 *   risks calling `verifyOtp` with the wrong `type`.
 * - "valid": both fields parse.
 */
export function parseResetPasswordHash(hash: string): ResetPasswordHashResult {
  const params = paramsFromHash(hash);
  const tokenHash = params.get("token_hash");
  if (tokenHash === null) {
    return { kind: "absent" };
  }
  const result = resetPasswordHashSchema.safeParse({
    token_hash: tokenHash,
    type: params.get("type") ?? undefined,
  });
  if (!result.success) {
    return { kind: "invalid" };
  }
  return { kind: "valid", token_hash: result.data.token_hash, type: result.data.type };
}

export interface HashAuthError {
  code: string | null;
  description: string | null;
}

/**
 * Captures a Supabase auth-error hash (`error_code=…` and/or
 * `error_description=…`), e.g. `#error=access_denied&error_code=otp_expired&…`.
 * Matches design.md decision 5's literal condition: presence is decided by
 * `error_code`/`error_description` specifically, not by a bare `error=`
 * alone (which never appears without one of the other two in practice).
 */
export function parseHashAuthError(hash: string): HashAuthError | null {
  const params = paramsFromHash(hash);
  const code = params.get("error_code");
  const description = params.get("error_description");
  if (code === null && description === null) {
    return null;
  }
  return { code, description };
}
